import { apiClient } from './client';
import type { Celebration, CreateCelebrationDto, UpdateCelebrationDto, CelebrationWithEvents, CelebrationPromotionReadiness, LandingThemeConfig } from '@/types/celebration.types';
import type {
  CelebrationDraftDto,
  CelebrationDraftEnvelope,
  CelebrationDraftMode,
  CelebrationDraftResumeState,
  CelebrationFeatureProfile,
  ChatEventSuggestionDto,
} from '@om/shared-types';

export interface CelebrationDraftPatch {
  draft?: CelebrationDraftDto;
  mode?: CelebrationDraftMode;
  resume_state?: CelebrationDraftResumeState;
}

export interface QuickCreateCelebrationDto {
  title: string;
  celebration_type: string;
  celebration_subtype?: string;
  template_key?: string;
  description?: string;
  location_name?: string;
  host_name?: string;
  host_email?: string;
  host_phone?: string;
  landing_theme?: LandingThemeConfig;
  rsvp_deadline?: string;
  max_guests?: number;
  plus_ones_allowed?: boolean;
  plus_ones_limit?: number;
  guest_list_visible?: boolean;
  access_type?: 'open' | 'invite_only' | 'registered';
  guest_mode?: 'link' | 'email' | 'detailed';
  guest_emails?: string[];
  email_dispatch_mode?: 'immediate' | 'draft';
  guest_count_estimate?: number;
  feature_profile?: CelebrationFeatureProfile;
  event_title?: string;
  event_type?: string;
  event_date: string;
  start_time: string;
  end_time: string;
  celebrant_names?: string[];
  celebrant_roles?: string[];
  celebrant_emails?: string[];
  is_surprise?: boolean;
}

export interface PublicQuickCreateCelebrationDto extends QuickCreateCelebrationDto {
  host_email?: string;
  host_phone?: string;
}

export interface QuickCreateCelebrationResponse {
  celebration: Celebration;
  event: {
    id: number;
    celebration_id: number;
    title: string;
    event_type: string;
    event_date: string;
    start_time: string;
    end_time: string;
    venue_name?: string;
    venue_location?: string;
    description?: string;
    status: string;
  };
  events?: QuickCreateCelebrationResponse['event'][];
  share_url: string;
  host_identity_sent_to?: string;
}

export interface LandingThemesResponse {
  templates: Array<{ id: string; name: string; description: string; sections: string[] }>;
  palettes: Array<{ id: string; name: string; description: string; previewSwatches: string[] }>;
  fontPairings: Array<{ id: string; name: string; description: string; headingFont: string; bodyFont: string; googleFontsUrl: string }>;
  defaults: LandingThemeConfig;
}

export interface CelebrationWorkflowMappingError {
  stage: string;
  code: string;
  message: string;
  details?: string;
  context?: Record<string, unknown>;
}

export interface CelebrationWorkflowMetrics {
  eventsCreated: number;
  activitiesCreated: number;
  goalsAttached: number;
  tasksCreated: number;
  servicesShortlisted: number;
  hasAssociatedGoalTemplates: boolean;
  hasAssociatedTaskTemplates: boolean;
}

export interface CreateCelebrationOrchestrationPayload extends Omit<CreateCelebrationDto, 'celebrants'> {
  confirmed_tags?: Record<string, string[]>;
  chat_session_id?: string;
  paragraph_content?: string;
  selected_events?: ChatEventSuggestionDto[];
  selected_activities?: Record<string, string[]>;
  selected_goal_template_ids_by_event?: Record<string, number[]>;
  create_goals_from_templates?: boolean;
  create_tasks_from_templates?: boolean;
  add_services_to_shortlist?: boolean;
  selected_service_definition_ids?: number[];
  celebrants?: Array<{
    user_id: number;
    role: string;
    is_surprise?: boolean;
  }>;
  celebrant_names?: string[];
  celebrant_roles?: string[];
  celebrant_emails?: string[];
}

export interface CreateCelebrationOrchestrationResponse {
  celebration: Celebration;
  warnings: string[];
  mappingErrors: CelebrationWorkflowMappingError[];
  metrics: CelebrationWorkflowMetrics;
  requestId?: string;
  logId?: string;
}

type CreateCelebrationApiResponse = {
  success: boolean;
  data: Celebration;
  warnings?: string[];
  mappingErrors?: CelebrationWorkflowMappingError[];
  metrics?: Partial<CelebrationWorkflowMetrics>;
  requestId?: string;
  logId?: string;
};

type QuickCreateCelebrationApiResponse = {
  success: boolean;
  data: QuickCreateCelebrationResponse;
  message?: string;
};

const getHeaderValue = (headers: unknown, headerName: string): string | undefined => {
  if (!headers || typeof headers !== 'object') {
    return undefined;
  }

  const normalizedHeaderName = headerName.toLowerCase();
  const headerMap = headers as Record<string, unknown>;
  const headerValue = headerMap[normalizedHeaderName] ?? headerMap[headerName];

  if (typeof headerValue === 'string' && headerValue.trim()) {
    return headerValue;
  }

  if (Array.isArray(headerValue) && headerValue.length > 0 && typeof headerValue[0] === 'string') {
    return headerValue[0];
  }

  return undefined;
};

const DEFAULT_WORKFLOW_METRICS: CelebrationWorkflowMetrics = {
  eventsCreated: 0,
  activitiesCreated: 0,
  goalsAttached: 0,
  tasksCreated: 0,
  servicesShortlisted: 0,
  hasAssociatedGoalTemplates: false,
  hasAssociatedTaskTemplates: false,
};

export class CelebrationService {
  async getCelebrations(): Promise<Celebration[]> {
    const response = await apiClient.get('/celebrations');
    return response.data.data || [];
  }

  async getMostRecentCelebration(): Promise<Celebration | null> {
    try {
      const celebrations = await this.getCelebrations();
      if (celebrations.length === 0) {
        return null;
      }
      
      // Filter for active celebrations (not soft-deleted — backend already excludes deleted)
      const activeCelebrations = celebrations.filter(celebration => 
        !celebration.deleted_at
      );
      
      // If we have active celebrations, return the most recent one
      if (activeCelebrations.length > 0) {
        const sorted = activeCelebrations.sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        return sorted[0];
      }
      
      // If no active celebrations, return the most recent overall
      const sorted = celebrations.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      
      return sorted[0];
    } catch (error) {
      console.error('Failed to get most recent celebration:', error);
      return null;
    }
  }

  async getCelebration(id: number): Promise<Celebration> {
    const response = await apiClient.get(`/celebrations/${id}`);
    return response.data.data;
  }

  async getCelebrationWithEvents(id: number): Promise<CelebrationWithEvents> {
    const response = await apiClient.get(`/celebrations/${id}?include_events=true`);
    return response.data.data;
  }

  async createCelebration(celebrationData: CreateCelebrationDto): Promise<Celebration> {
    const response = await apiClient.post('/celebrations', celebrationData);
    return response.data.data;
  }

  async quickCreateCelebration(payload: QuickCreateCelebrationDto): Promise<QuickCreateCelebrationResponse> {
    const response = await apiClient.post<QuickCreateCelebrationApiResponse>('/celebrations/quick', payload);
    return response.data.data;
  }

  async quickCreatePublicCelebration(payload: PublicQuickCreateCelebrationDto): Promise<QuickCreateCelebrationResponse> {
    const response = await apiClient.post<QuickCreateCelebrationApiResponse>('/public/quick-celebrations', payload);
    return response.data.data;
  }

  async createDraft(patch: CelebrationDraftPatch = {}): Promise<CelebrationDraftEnvelope> {
    const response = await apiClient.post<{ success: boolean; data: CelebrationDraftEnvelope }>('/celebration-drafts', patch);
    return response.data.data;
  }

  async listDrafts(): Promise<CelebrationDraftEnvelope[]> {
    const response = await apiClient.get<{ success: boolean; data: CelebrationDraftEnvelope[] }>('/celebration-drafts');
    return response.data.data;
  }

  async getDraft(draftId: string): Promise<CelebrationDraftEnvelope> {
    const response = await apiClient.get<{ success: boolean; data: CelebrationDraftEnvelope }>(`/celebration-drafts/${draftId}`);
    return response.data.data;
  }

  async patchDraft(draftId: string, patch: CelebrationDraftPatch): Promise<CelebrationDraftEnvelope> {
    const response = await apiClient.patch<{ success: boolean; data: CelebrationDraftEnvelope }>(`/celebration-drafts/${draftId}`, patch);
    return response.data.data;
  }

  async completeDraft(draftId: string): Promise<void> {
    await apiClient.post(`/celebration-drafts/${draftId}/complete`);
  }

  async deleteDraft(draftId: string): Promise<void> {
    await apiClient.delete(`/celebration-drafts/${draftId}`);
  }

  async createCelebrationWithOrchestration(
    payload: CreateCelebrationOrchestrationPayload,
  ): Promise<CreateCelebrationOrchestrationResponse> {
    const response = await apiClient.post<CreateCelebrationApiResponse>('/celebrations', payload);
    const responseData = response.data;
    const payloadRequestId = typeof responseData.requestId === 'string' ? responseData.requestId : undefined;
    const payloadLogId = typeof responseData.logId === 'string' ? responseData.logId : undefined;
    const headerRequestId = getHeaderValue(response.headers, 'x-request-id');
    const headerLogId = getHeaderValue(response.headers, 'x-log-id');
    const logId = payloadLogId || payloadRequestId || headerLogId || headerRequestId;

    return {
      celebration: responseData.data,
      warnings: Array.isArray(responseData.warnings) ? responseData.warnings : [],
      mappingErrors: Array.isArray(responseData.mappingErrors) ? responseData.mappingErrors : [],
      metrics: {
        ...DEFAULT_WORKFLOW_METRICS,
        ...(responseData.metrics ?? {}),
      },
      requestId: payloadRequestId || headerRequestId,
      logId,
    };
  }

  async updateCelebration(id: number, celebrationData: UpdateCelebrationDto): Promise<Celebration> {
    const response = await apiClient.put(`/celebrations/${id}`, celebrationData);
    return response.data.data;
  }

  async promoteCelebration(id: number): Promise<Celebration> {
    const response = await apiClient.post(`/celebrations/${id}/promote`);
    return response.data.data;
  }

  async getPromotionReadiness(id: number): Promise<CelebrationPromotionReadiness> {
    const response = await apiClient.get(`/celebrations/${id}/promotion-readiness`);
    return response.data.data;
  }

  async deleteCelebration(id: number): Promise<void> {
    if (!id || id === undefined || isNaN(Number(id)) || id <= 0) {
      throw new Error(`Invalid celebration ID for deletion: ${id}`);
    }
    
    await apiClient.delete(`/celebrations/${id}`);
  }

  async getTemplates(): Promise<Array<{key: string, label: string, type: string, subtype?: string, description: string}>> {
    const response = await apiClient.get('/celebrations/templates');
    return response.data.data || [];
  }

  async getLandingThemes(): Promise<LandingThemesResponse> {
    const response = await apiClient.get('/celebrations/landing-themes');
    return response.data.data;
  }

  async generateHostMessage(celebrationId: number): Promise<string> {
    const response = await apiClient.post<{ success: boolean; data: { message: string } }>(
      `/celebrations/${celebrationId}/generate-host-message`
    );
    return response.data.data.message;
  }
}

export const celebrationService = new CelebrationService();
